/**
 * Sugestões clicáveis do Assistente da Plataforma.
 *
 * Filtra pelo escopo do usuário (cargo → permissions), pelo segmento do
 * workspace e pelos canais realmente configurados — não oferece "Enviar
 * WhatsApp" sem canal WhatsApp ativo, por exemplo.
 *
 * A lista final é montada por rodízio entre grupos (analytics, export, write…)
 * para que PDF/planilha/métricas não sumam atrás só de ações de escrita.
 */

import { WorkspaceSegments } from "../constants/workspaceSegments";
import { CHANNEL_TYPES } from "../constants/channelCatalog";

/** Evita importar hooks (e o client HTTP) — só a semântica de escopo usada aqui. */
function toSet(scopes) {
  return scopes instanceof Set ? scopes : new Set(Array.isArray(scopes) ? scopes : []);
}

function can(set, resource, action = "view") {
  if (set.has(`${resource}.${action}`)) return true;
  if (resource === "passagens" && action === "view" && set.has("bpe.view")) return true;
  if (resource === "team_tasks" && action === "view") {
    return set.has("team_chat.view") || set.has("chat.view");
  }
  // Retrocompat alinhada a useEffectivePermissions: clients.view cobre manage na UI.
  if (resource === "clients" && action === "manage" && set.has("clients.view")) return true;
  return false;
}

/** Tipos alinhados ao outbound do assistente (API). */
export const ASSISTANT_WHATSAPP_CHANNEL_TIPOS = new Set([
  CHANNEL_TYPES.WHATSAPP_QR,
  CHANNEL_TYPES.WHATSAPP_GUPSHUP,
  CHANNEL_TYPES.WHATSAPP_CLOUD,
]);

export const ASSISTANT_EMAIL_CHANNEL_TIPOS = new Set([
  CHANNEL_TYPES.MAILGUN,
  CHANNEL_TYPES.SMTP,
  CHANNEL_TYPES.EMAIL_AWS_SES,
]);

/**
 * @param {Array<{ tipo?: number|string, excluido?: number|boolean }>|null|undefined} channels
 * @returns {{ channelsLoaded: boolean, hasWhatsapp: boolean, hasEmail: boolean }}
 */
export function deriveAssistantChannelCapabilities(channels) {
  if (!Array.isArray(channels)) {
    return { channelsLoaded: false, hasWhatsapp: false, hasEmail: false };
  }
  let hasWhatsapp = false;
  let hasEmail = false;
  for (const ch of channels) {
    if (ch?.excluido === 1 || ch?.excluido === true) continue;
    const tipo = Number(ch?.tipo);
    if (!Number.isFinite(tipo)) continue;
    if (ASSISTANT_WHATSAPP_CHANNEL_TIPOS.has(tipo)) hasWhatsapp = true;
    if (ASSISTANT_EMAIL_CHANNEL_TIPOS.has(tipo)) hasEmail = true;
  }
  return { channelsLoaded: true, hasWhatsapp, hasEmail };
}

/**
 * @typedef {{
 *   id: string,
 *   promptKey: string,
 *   labelKey: string,
 *   group?: string,
 *   requires?: (ctx: object) => boolean
 * }} SuggestionDef
 */

const canChat = (scopes) => can(scopes, "chat", "view") || can(scopes, "chat", "update");
const canDashboardOrChat = (scopes) => can(scopes, "dashboard", "view") || canChat(scopes);
const canTeamWrite = (scopes) =>
  can(scopes, "team_tasks", "view") || can(scopes, "team_chat", "view") || canChat(scopes);

/** @type {SuggestionDef[]} */
const WORKSPACE_SUGGESTIONS = [
  // —— Consultas / analytics ——
  {
    id: "atendimento-resumo",
    group: "analytics",
    labelKey: "platformAi.suggestAtendimentoResumo",
    promptKey: "platformAi.suggestAtendimentoResumoPrompt",
    requires: ({ scopes }) => canDashboardOrChat(scopes),
  },
  {
    id: "operadores",
    group: "analytics",
    labelKey: "platformAi.suggestOperadores",
    promptKey: "platformAi.suggestOperadoresPrompt",
    requires: ({ scopes }) => canDashboardOrChat(scopes),
  },
  {
    id: "vendas",
    group: "analytics",
    labelKey: "platformAi.suggestVendas",
    promptKey: "platformAi.suggestVendasPrompt",
    requires: ({ scopes, segment }) =>
      segment !== WorkspaceSegments.PassengerTransport &&
      (can(scopes, "sales_pipeline", "view") || can(scopes, "dashboard", "view")),
  },
  {
    id: "passagens",
    group: "analytics",
    labelKey: "platformAi.suggestPassagens",
    promptKey: "platformAi.suggestPassagensPrompt",
    requires: ({ scopes, segment }) =>
      segment === WorkspaceSegments.PassengerTransport &&
      (can(scopes, "passagens", "view") ||
        can(scopes, "bpe", "view") ||
        can(scopes, "dashboard", "view")),
  },
  {
    id: "clientes-novos",
    group: "analytics",
    labelKey: "platformAi.suggestClientesNovos",
    promptKey: "platformAi.suggestClientesNovosPrompt",
    requires: ({ scopes }) => can(scopes, "clients", "view"),
  },
  {
    id: "pesquisas",
    group: "analytics",
    labelKey: "platformAi.suggestPesquisas",
    promptKey: "platformAi.suggestPesquisasPrompt",
    requires: ({ scopes }) => can(scopes, "surveys", "view"),
  },
  {
    id: "canais",
    group: "analytics",
    labelKey: "platformAi.suggestCanais",
    promptKey: "platformAi.suggestCanaisPrompt",
    requires: ({ scopes }) => can(scopes, "channels", "view"),
  },

  // —— Busca qualitativa / registros ——
  {
    id: "atendimento-reclamacoes",
    group: "lookup",
    labelKey: "platformAi.suggestReclamacoes",
    promptKey: "platformAi.suggestReclamacoesPrompt",
    requires: ({ scopes }) => canChat(scopes) || can(scopes, "dashboard", "view"),
  },
  {
    id: "find-client",
    group: "lookup",
    labelKey: "platformAi.suggestFindClient",
    promptKey: "platformAi.suggestFindClientPrompt",
    requires: ({ scopes }) => can(scopes, "clients", "view"),
  },

  // —— Exportações ——
  {
    id: "pdf-atendimento",
    group: "export",
    labelKey: "platformAi.suggestPdfAtendimento",
    promptKey: "platformAi.suggestPdfAtendimentoPrompt",
    requires: ({ scopes }) => canDashboardOrChat(scopes),
  },
  {
    id: "xlsx-atendimento",
    group: "export",
    labelKey: "platformAi.suggestXlsxAtendimento",
    promptKey: "platformAi.suggestXlsxAtendimentoPrompt",
    requires: ({ scopes }) => canDashboardOrChat(scopes),
  },
  {
    id: "pdf-clientes",
    group: "export",
    labelKey: "platformAi.suggestPdfClientes",
    promptKey: "platformAi.suggestPdfClientesPrompt",
    requires: ({ scopes }) => can(scopes, "clients", "view"),
  },
  {
    id: "xlsx-clientes",
    group: "export",
    labelKey: "platformAi.suggestXlsxClientes",
    promptKey: "platformAi.suggestXlsxClientesPrompt",
    requires: ({ scopes }) => can(scopes, "clients", "view"),
  },

  // —— Escrita (propose → confirm) ——
  {
    id: "create-task",
    group: "write",
    labelKey: "platformAi.suggestCreateTask",
    promptKey: "platformAi.suggestCreateTaskPrompt",
    requires: ({ scopes }) => canTeamWrite(scopes),
  },
  {
    id: "create-appointment",
    group: "write",
    labelKey: "platformAi.suggestCreateAppointment",
    promptKey: "platformAi.suggestCreateAppointmentPrompt",
    requires: ({ scopes }) => can(scopes, "team_chat", "view") || canChat(scopes),
  },
  {
    id: "create-client",
    group: "write",
    labelKey: "platformAi.suggestCreateClient",
    promptKey: "platformAi.suggestCreateClientPrompt",
    requires: ({ scopes }) => can(scopes, "clients", "manage"),
  },
  {
    id: "create-request",
    group: "write",
    labelKey: "platformAi.suggestCreateRequest",
    promptKey: "platformAi.suggestCreateRequestPrompt",
    requires: ({ scopes }) => canChat(scopes),
  },
  {
    id: "create-user",
    group: "write",
    labelKey: "platformAi.suggestCreateUser",
    promptKey: "platformAi.suggestCreateUserPrompt",
    requires: ({ scopes }) => can(scopes, "users", "create"),
  },

  // —— Outbound (só com canal configurado) ——
  {
    id: "send-whatsapp",
    group: "outbound",
    labelKey: "platformAi.suggestSendWhatsapp",
    promptKey: "platformAi.suggestSendWhatsappPrompt",
    requires: ({ scopes, channels }) =>
      canChat(scopes) && channels?.channelsLoaded === true && channels.hasWhatsapp === true,
  },
  {
    id: "send-email",
    group: "outbound",
    labelKey: "platformAi.suggestSendEmail",
    promptKey: "platformAi.suggestSendEmailPrompt",
    requires: ({ scopes, channels }) =>
      canChat(scopes) && channels?.channelsLoaded === true && channels.hasEmail === true,
  },

  // —— Ajuda ——
  {
    id: "ajuda-whatsapp",
    group: "help",
    labelKey: "platformAi.suggestAjudaWhatsapp",
    promptKey: "platformAi.suggestAjudaWhatsappPrompt",
    // Só faz sentido quando ainda não há WhatsApp e a pessoa pode ver canais.
    requires: ({ scopes, channels }) =>
      channels?.channelsLoaded === true &&
      channels.hasWhatsapp === false &&
      (can(scopes, "channels", "view") || can(scopes, "channels", "create")),
  },
];

/** @type {SuggestionDef[]} */
const PARTNER_SUGGESTIONS = [
  {
    id: "partner-carteira",
    group: "analytics",
    labelKey: "platformAi.suggestPartnerCarteira",
    promptKey: "platformAi.suggestPartnerCarteiraPrompt",
    requires: () => true,
  },
  {
    id: "partner-presenca",
    group: "analytics",
    labelKey: "platformAi.suggestPartnerPresenca",
    promptKey: "platformAi.suggestPartnerPresencaPrompt",
    requires: () => true,
  },
  {
    id: "partner-trial",
    group: "write",
    labelKey: "platformAi.suggestPartnerTrial",
    promptKey: "platformAi.suggestPartnerTrialPrompt",
    requires: () => true,
  },
];

const GROUP_ORDER = Object.freeze([
  "analytics",
  "lookup",
  "export",
  "write",
  "outbound",
  "help",
  "other",
]);

const MAX_SUGGESTIONS = 10;

/**
 * Intercala grupos para cobrir o leque de capacidades do assistente.
 * @param {SuggestionDef[]} eligible
 * @param {number} max
 */
export function pickBalancedSuggestions(eligible, max = MAX_SUGGESTIONS) {
  const groups = new Map();
  for (const item of eligible) {
    const g = item.group || "other";
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g).push(item);
  }

  const queues = [];
  for (const g of GROUP_ORDER) {
    if (groups.has(g)) queues.push(groups.get(g));
  }
  for (const [g, list] of groups) {
    if (!GROUP_ORDER.includes(g)) queues.push(list);
  }

  const out = [];
  const limit = Math.max(0, Number(max) || 0);
  while (out.length < limit) {
    let progressed = false;
    for (const q of queues) {
      if (out.length >= limit) break;
      if (q.length) {
        out.push(q.shift());
        progressed = true;
      }
    }
    if (!progressed) break;
  }
  return out;
}

/**
 * @param {{
 *   scopes?: string[]|Set<string>,
 *   segment?: string|null,
 *   partner?: boolean,
 *   channels?: Array|null,
 *   channelCapabilities?: { channelsLoaded?: boolean, hasWhatsapp?: boolean, hasEmail?: boolean }|null,
 *   max?: number,
 * }} opts
 * @returns {SuggestionDef[]}
 */
export function buildAssistantSuggestions({
  scopes = [],
  segment = null,
  partner = false,
  channels = null,
  channelCapabilities = null,
  max = MAX_SUGGESTIONS,
} = {}) {
  const scopeSet = scopes instanceof Set ? scopes : toSet(scopes);
  const channelCaps =
    channelCapabilities && typeof channelCapabilities === "object"
      ? {
          channelsLoaded: !!channelCapabilities.channelsLoaded,
          hasWhatsapp: !!channelCapabilities.hasWhatsapp,
          hasEmail: !!channelCapabilities.hasEmail,
        }
      : deriveAssistantChannelCapabilities(channels);

  const catalog = partner ? PARTNER_SUGGESTIONS : WORKSPACE_SUGGESTIONS;
  const ctx = {
    scopes: scopeSet,
    segment: segment || null,
    partner: !!partner,
    channels: channelCaps,
  };

  const eligible = catalog.filter((item) => {
    try {
      return !item.requires || item.requires(ctx);
    } catch {
      return false;
    }
  });

  return pickBalancedSuggestions(eligible, max);
}

export { WORKSPACE_SUGGESTIONS, PARTNER_SUGGESTIONS, MAX_SUGGESTIONS, GROUP_ORDER };
